/*
  Convert a serializated position into a lexicographically sortable string (a "lexposition"), and
  vice-versa.

  There is a python and JS implementaton. This file MUST be kept in sync with lexposition.py

  The tests in Python and JS must also be kept in sync. They are driven from a single JSON file
  containing fixtures.

  As you can see from the test cases, this code follows the robustness principle;
  "Be conservative in what you send, be liberal in what you accept."

  Note on Base-62:
  When converting to base-62, we treat the input purely as a number and convert it directly into Base-62
  by repeatedly dividing by 62 and taking the remainder, partly because this produces the shortest
  output. For example, 932 converts to F2.
  To verify our numeric approach, use https://math.tools/calculator/base/10-62. If you try 932 in most
  Base62 encoder sites, they’ll first treat it as ASCII bytes (0x33 0x33 0x32), form a huge integer
  (0x333332 = 3 348 886), then Base-62‐encode that, yielding “FjCE”.

 Note on path segment order:
 In serialized positions, the node indexes in the path are in reverse order.
 This was a mistake. In lexpositions, they are not in reverse order.
*/

import { convertStringIndexToIntegerAndValidate, parseSerializedPosition, serializedPositionOffsetDelimiter, serializedPositionPathSegmentDelimiter } from './locationSerialization';

const alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const lexpositionOffsetDelimiter = '-';

/*
 The offset is left-padded to this many digits (so that 10 doesn't come before 2 when sorting). It is
 the max-length of any given text-node index. Note: this is after (Base-62) compression so it's larger
 than you'd think
*/
const lexpositionOffsetLength = 4;
const lexpositionOffsetMaxValue = new Array(lexpositionOffsetLength).fill(alphabet[alphabet.length - 1]).join('');
const lexpositionOffsetMaxValueAsBase10 = _convertBase62ToInt(lexpositionOffsetMaxValue);
const lexpositionPathSegmentDelimiter = '_';

// Path indexes are left-padded to this many digits (so that 10 doesn't come before 2 when sorting).
const lexpositionPathIndexLength = 5;
const lexpositionPathIndexMaxValue = new Array(lexpositionPathIndexLength).fill(alphabet[alphabet.length - 1]).join('');
const lexpositionPathIndexMaxValueAsBase10 = _convertBase62ToInt(lexpositionPathIndexMaxValue);

/*
  Convert a lexposition node index into the serialized position node index format.
  E.g. "F2" -> "932"
*/
function _convertLexpositionNodeIndexToSerializedPositionNodeIndex(base62: string): string {
  return _convertBase62ToInt(base62).toString();
}

/*
  Convert a serialized position node index into the lexposition node index format.
  E.g. "932" -> "F2"
*/
function _convertSerializedPositionNodeIndexToLexpositionNodeIndex({
  length,
  maxValue,
  name,
  nodeIndex,
}: {
  length: number;
  maxValue: number;
  name: string;
  nodeIndex: string;
}): string {
  const integer = convertStringIndexToIntegerAndValidate({
    maxValue,
    name,
    nodeIndex,
  });
  return _convertIntToBase62(integer).padStart(length, '0');
}

/*
  E.g. "F2" -> 932
*/
export function _convertBase62ToInt(base62: string): number {
  if (typeof base62 !== 'string') {
    throw new Error('input not a string');
  }

  const trimmedInput = base62.trim();
  if (!trimmedInput) {
    throw new Error('(trimmed) input string is empty');
  }

  let total = 0;
  for (const character of trimmedInput.split('')) {
    const index = alphabet.indexOf(character);
    if (index === -1) {
      throw new Error(`invalid character '${character}' in input string`);
    }
    total = total * 62 + index;
  }
  return total;
}

/*
  E.g. 932 -> "F2"
*/
export function _convertIntToBase62(number: number): string {
  if (!Number.isInteger(number)) {
    throw new Error('input is not an int');
  }
  if (number < 0) {
    throw new Error('negative ints not supported');
  }

  if (number === 0) {
    return alphabet[0];
  }

  const characters: string[] = [];
  let _number = number;
  while (_number) {
    const remainder = _number % 62;
    _number = Math.floor(_number / 62);
    characters.push(alphabet[remainder]);
  }
  characters.reverse();
  return characters.join('');
}

/*
  E.g. "0000X_0000M_0000B-000l" -> "11/22/33:47"
*/
export function convertLexpositionToSerializedPosition(lexposition: string): string {
  if (typeof lexposition !== 'string') {
    throw new Error('input is not a string');
  }

  const cleanedInput = lexposition.trim();
  if (!cleanedInput) {
    throw new Error('(trimmed) input string is empty');
  }

  let path: string;
  let offset: string;

  if (cleanedInput.includes(lexpositionOffsetDelimiter)) {
    const pieces = cleanedInput.split(lexpositionOffsetDelimiter);
    path = pieces[0];
    // `split` has `limit` parameter but it doesn't work as expected / satisfy our tests
    offset = pieces.slice(1).join(lexpositionOffsetDelimiter);
  } else {
    path = cleanedInput;
    offset = '';
  }

  let result = '';

  if (path) {
    const pathNodeIndexes = path.split(lexpositionPathSegmentDelimiter);
    const outputNodeIndexes = [];
    pathNodeIndexes.reverse();

    for (const nodeIndex of pathNodeIndexes) {
      const trimmedSegment = nodeIndex.trim();
      if (!trimmedSegment) {
        continue;
      }
      outputNodeIndexes.push(_convertLexpositionNodeIndexToSerializedPositionNodeIndex(trimmedSegment));
    }

    result += outputNodeIndexes.join(serializedPositionPathSegmentDelimiter);
  }

  if (offset) {
    const trimmedOffset = offset.trim();
    if (trimmedOffset) {
      const outputOffset = _convertLexpositionNodeIndexToSerializedPositionNodeIndex(trimmedOffset);
      result = [result, outputOffset].join(serializedPositionOffsetDelimiter);
    }
  }

  return result;
}

/*
  E.g. "11/22/33:47" -> "0000X_0000M_0000B-000l"
*/
export function convertSerializedPositionToLexpositionTo(serializedPosition: string): string {
  const { nodeIndices, offset } = parseSerializedPosition(serializedPosition, {
    pathIndexMaxValue: lexpositionPathIndexMaxValueAsBase10,
    offsetMaxValue: lexpositionOffsetMaxValueAsBase10,
  });
  let result = '';

  if (nodeIndices.length) {
    const outputNodeIndexes: string[] = [];
    nodeIndices.reverse();
    for (const nodeIndex of nodeIndices) {
      outputNodeIndexes.push(_convertSerializedPositionNodeIndexToLexpositionNodeIndex({
        length: lexpositionPathIndexLength,
        maxValue: lexpositionPathIndexMaxValueAsBase10,
        name: 'path segment',
        // This function validates too, so let's just pass it as a string, rather than complicate the code
        nodeIndex: `${nodeIndex}`,
      }));
    }
    result += outputNodeIndexes.join(lexpositionPathSegmentDelimiter);
  }

  if (typeof offset === 'number') {
    const outputOffset = _convertSerializedPositionNodeIndexToLexpositionNodeIndex({
      length: lexpositionOffsetLength,
      maxValue: lexpositionOffsetMaxValueAsBase10,
      name: 'offset',
      // This function validates too, so let's just pass it as a string, rather than complicate the code
      nodeIndex: `${offset}`,
    });
    result = [result, outputOffset].join(lexpositionOffsetDelimiter);
  }

  return result;
}
